'use client';

import React, { useEffect, useRef, useState } from 'react';

interface FeedbackForm {
  name: string;
  rating: number;
  message: string;
}

const STAR_LABELS = ['Poor', 'Fair', 'Good', 'Great', 'Excellent'];

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [form, setForm] = useState<FeedbackForm>({ name: '', rating: 0, message: '' });
  const [hovered, setHovered] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<FeedbackForm>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.1 }
    );
    const items = sectionRef.current?.querySelectorAll('.reveal-up');
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const validate = () => {
    const newErrors: Partial<FeedbackForm> = {};
    if (!form.name.trim()) newErrors.name = 'Please enter your name.';
    if (!form.rating) newErrors.rating = 'Please select a rating.' as any;
    if (!form.message.trim()) newErrors.message = 'Please write a message.';
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitted(true);
  };

  const handleReset = () => {
    setForm({ name: '', rating: 0, message: '' });
    setHovered(0);
    setSubmitted(false);
    setErrors({});
  };

  return (
    <section
      ref={sectionRef}
      className="block-concrete py-24 px-6 md:px-10"
      aria-label="Client feedback">

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16 reveal-up">
          <p className="section-label text-charcoal/40 mb-4">§ 03 — Voices</p>
          <h2
            className="font-mono font-bold uppercase leading-[0.9] tracking-tight text-charcoal"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 4.5rem)' }}>
            Share Your<br />
            <span className="text-coral">Experience.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Left — blank display area */}
          <div className="reveal-up">
            <div
              className="border border-charcoal/10 p-10 md:p-14 min-h-[320px] flex flex-col items-center justify-center text-center"
              style={{ background: '#E0DCD6' }}>
              <span
                className="font-mono font-bold text-charcoal/10 select-none"
                style={{ fontSize: '6rem', lineHeight: 1 }}
                aria-hidden="true">
                &ldquo;
              </span>
              <p className="font-mono text-sm uppercase tracking-widest text-charcoal/30 mt-4">
                No feedback yet.
              </p>
              <p className="font-sans text-xs text-charcoal/25 mt-2">
                Be the first to share your experience.
              </p>
            </div>
          </div>

          {/* Right — feedback form */}
          <div className="reveal-up delay-1">
            {submitted ? (
              <div className="border border-charcoal/10 p-10 flex flex-col items-center justify-center text-center min-h-[320px]">
                <div className="w-12 h-12 rounded-full border-2 border-coral flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-coral" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-mono text-sm font-bold uppercase tracking-wide text-charcoal mb-2">
                  Thank you, {form.name}!
                </p>
                <p className="font-sans text-sm text-charcoal/50 mb-8">
                  Your feedback means a lot.
                </p>
                <button
                  onClick={handleReset}
                  className="font-mono text-xs uppercase tracking-widest text-coral border border-coral px-6 py-2 hover:bg-coral hover:text-white transition-colors duration-200">
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="border border-charcoal/10 p-8 md:p-10 space-y-6">
                <p className="font-mono text-xs uppercase tracking-widest text-charcoal/40 mb-2">
                  Leave Feedback
                </p>

                {/* Name */}
                <div>
                  <label htmlFor="feedback-name" className="block font-mono text-xs uppercase tracking-widest text-charcoal mb-2">
                    Your Name
                  </label>
                  <input
                    id="feedback-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Jane Doe"
                    className={`w-full bg-transparent border ${errors.name ? 'border-red-400' : 'border-charcoal/20'} px-4 py-3 font-sans text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-coral transition-colors`}
                  />
                  {errors.name && (
                    <p className="mt-1 font-sans text-xs text-red-400">{errors.name}</p>
                  )}
                </div>

                {/* Rating */}
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-charcoal mb-3">
                    Rating
                  </p>
                  <div className="flex gap-2" role="radiogroup" aria-label="Rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        role="radio"
                        aria-checked={form.rating === star}
                        aria-label={STAR_LABELS[star - 1]}
                        onClick={() => setForm({ ...form, rating: star })}
                        onMouseEnter={() => setHovered(star)}
                        onMouseLeave={() => setHovered(0)}
                        className="text-2xl transition-transform duration-100 hover:scale-110 focus:outline-none">
                        <span className={(hovered || form.rating) >= star ? 'text-coral' : 'text-charcoal/20'}>
                          ★
                        </span>
                      </button>
                    ))}
                    {(hovered || form.rating) > 0 && (
                      <span className="ml-2 font-mono text-xs text-charcoal/40 self-center">
                        {STAR_LABELS[(hovered || form.rating) - 1]}
                      </span>
                    )}
                  </div>
                  {errors.rating && (
                    <p className="mt-1 font-sans text-xs text-red-400">{errors.rating as string}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="feedback-message" className="block font-mono text-xs uppercase tracking-widest text-charcoal mb-2">
                    Message
                  </label>
                  <textarea
                    id="feedback-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your experience…"
                    className={`w-full bg-transparent border ${errors.message ? 'border-red-400' : 'border-charcoal/20'} px-4 py-3 font-sans text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-coral transition-colors resize-none`}
                  />
                  {errors.message && (
                    <p className="mt-1 font-sans text-xs text-red-400">{errors.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full font-mono text-xs uppercase tracking-widest bg-charcoal text-white py-4 hover:bg-coral transition-colors duration-200">
                  Submit Feedback
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}